import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import {
    Bold,
    Italic,
    List,
    ListOrdered,
    Link as LinkIcon,
    Palette,
    Heading2,
    Undo,
    Redo
} from 'lucide-react';
import Button from './Button';
import { useState } from 'react';
import '../../../Assets/Styles/RichTextEditor.css';

export const RichTextEditor = ({ content = '', onChange, placeholder }) => {
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [customColor, setCustomColor] = useState('#000000');

    const editor = useEditor({
        extensions: [
            StarterKit,
            Link.configure({
                openOnClick: false,
                HTMLAttributes: {
                    class: 'rte-link',
                },
            }),
            TextStyle,
            Color,
        ],
        content: content || '<p></p>',
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            onChange && onChange(html);
        },
        editorProps: {
            attributes: {
                class: 'rte-editor-area',
            },
            handleDOMEvents: {
                // ✅ Empêche que les inputs internes fassent "submit"
                keydown: (view, event) => {
                    if (event.key === 'Enter' && event.target.tagName === 'INPUT') {
                        event.stopPropagation();
                        return true;
                    }
                    return false;
                },
            },
        },
    });

    if (!editor) return null;

    const colors = [
        '#000000', '#374151', '#DC2626', '#EA580C', '#D97706',
        '#65A30D', '#059669', '#0891B2', '#2563EB', '#7C3AED',
        '#C026D3', '#DB2777'
    ];

    const addLink = () => {
        if (linkUrl.trim()) {
            editor.chain().focus().setLink({ href: linkUrl }).run();
            setLinkUrl('');
            setShowLinkInput(false);
        }
    };

    const removeLink = () => {
        editor.chain().focus().unsetLink().run();
        setShowLinkInput(false);
    };

    return (
        <div className="rte-container">
            {/* Toolbar */}
            <div className="rte-toolbar">
                {/* Button en gras */}
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                        const isActive = editor.isActive('bold');
                        editor.chain().focus().toggleBold().run();

                        if (!isActive) {
                            editor.chain().focus().setMark('bold').run();
                        } else {
                            editor.chain().focus().unsetMark('bold').run();
                        }
                    }}
                    className={editor.isActive('bold') ? 'rte-btn-active' : ''}
                >
                    <Bold size={16} />
                </Button>


                <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'rte-btn-active' : ''}>
                    <Italic size={16} />
                </Button>

                <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={editor.isActive('heading', { level: 2 }) ? 'rte-btn-active' : ''}>
                    <Heading2 size={16} />
                </Button>

                <div className="rte-divider" />

                <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'rte-btn-active' : ''}>
                    <List size={16} />
                </Button>

                <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive('orderedList') ? 'rte-btn-active' : ''}>
                    <ListOrdered size={16} />
                </Button>

                <div className="rte-divider" />

                {/* Liens */}
                <div className="rte-relative">
                    <Button variant="ghost" size="sm" onClick={() => setShowLinkInput(!showLinkInput)} className={editor.isActive('link') ? 'rte-btn-active' : ''}>
                        <LinkIcon size={16} />
                    </Button>

                    {showLinkInput && (
                        <div className="rte-popover" onClick={(e) => e.stopPropagation()}>
                            <input
                                type="url"
                                placeholder="https://exemple.com"
                                value={linkUrl}
                                onChange={(e) => setLinkUrl(e.target.value)}
                                className="rte-input"
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addLink();
                                    }
                                }}
                            />
                            <div className="rte-flex">
                                <Button size="sm" onClick={addLink} className="rte-flex-1">
                                    Ajouter
                                </Button>
                                {editor.isActive('link') && (
                                    <Button size="sm" variant="destructive" onClick={removeLink}>
                                        Supprimer
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Couleurs */}
                {/* Couleurs */}
                <div className="rte-relative">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowColorPicker(!showColorPicker)}
                    >
                        <Palette size={16} />
                    </Button>

                    {showColorPicker && (
                        <div className="rte-popover" onClick={(e) => e.stopPropagation()}>
                            <div className="rte-color-grid">
                                {colors.map((color) => (
                                    <button
                                        key={color}
                                        type="button"
                                        className="rte-color-btn"
                                        style={{ backgroundColor: color }}
                                        onClick={() => {
                                            editor.chain().focus().setColor(color).run();
                                            setShowColorPicker(false);
                                        }}
                                    />
                                ))}
                            </div>

                            {/* ✅ Section couleur personnalisée */}
                            <div className="rte-custom-color">
                                {/* Sélecteur visuel */}
                                <input
                                    type="color"
                                    value={customColor}
                                    onChange={(e) => {
                                        const newColor = e.target.value;
                                        setCustomColor(newColor);
                                        editor.chain().focus().setColor(newColor).run();
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                />

                                {/* Entrée manuelle HEX/RGB */}
                                <input
                                    type="text"
                                    value={customColor}
                                    onChange={(e) => {
                                        const newColor = e.target.value.trim();
                                        setCustomColor(newColor);
                                    }}
                                    onBlur={() => {
                                        // ✅ Quand on quitte le champ, on applique la couleur
                                        if (customColor) editor.chain().focus().setColor(customColor).run();
                                    }}
                                    placeholder="#000000 ou rgb(255,0,0)"
                                    className="rte-color-input"
                                    onClick={(e) => e.stopPropagation()}
                                />

                                <Button
                                    size="sm"
                                    onClick={() => {
                                        editor.chain().focus().setColor(customColor).run();
                                        setShowColorPicker(false);
                                    }}
                                    className="rte-flex-1"
                                >
                                    Appliquer
                                </Button>
                            </div>

                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                    editor.chain().focus().unsetColor().run();
                                    setShowColorPicker(false);
                                }}
                                className="rte-btn-outline"
                            >
                                Réinitialiser
                            </Button>
                        </div>
                    )}
                </div>



                <div className="rte-divider" />

                <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
                    <Undo size={16} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
                    <Redo size={16} />
                </Button>
            </div>

            {/* Zone de saisie */}
            <EditorContent editor={editor} className="rte-editor" placeholder={placeholder} />
        </div>
    );
};

export default RichTextEditor;
